import { Suspense } from 'react';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { Navigation } from './components/layout/Navigation';
import { ErrorBoundary } from './components/layout/ErrorBoundary';
import { LoadingSpinner } from './components/common';
import { useFeatureRegistry } from './features';
import { AppNavProvider, useAppNav, AppTab } from './app/nav';
import styles from './App.module.scss';

/**
 * Tab shell. All features stay MOUNTED and inactive panels are hidden with the
 * `hidden` attribute — deliberately not conditional rendering: unmounting would
 * drop AiFeatures' in-flight generation/preview state and Textbearbeitung's
 * Alt-shortcut listener. Switching tabs mid-generation is loss-free.
 */
const AppShell: React.FC = () => {
  const features = useFeatureRegistry();
  const { tab, setTab } = useAppNav();

  return (
    <div className={styles.app}>
      <ErrorBoundary>
        <div className={styles.body}>
          <Navigation
            features={features}
            activeFeatureId={tab}
            onFeatureSelect={(id) => setTab(id as AppTab)}
          />
          <div className={styles.content}>
            {features.map((feature) => {
              const FeatureComponent = feature.component;
              return (
                <div
                  key={feature.id}
                  role="tabpanel"
                  aria-label={feature.name}
                  hidden={feature.id !== tab}
                  className={styles.tabPanel}
                >
                  <Suspense fallback={<LoadingSpinner />}>
                    <FeatureComponent />
                  </Suspense>
                </div>
              );
            })}
          </div>
        </div>
      </ErrorBoundary>
    </div>
  );
};

const App: React.FC = () => (
  <FluentProvider theme={webLightTheme}>
    <AppNavProvider>
      <AppShell />
    </AppNavProvider>
  </FluentProvider>
);

export default App;
